<template>
	<div>
		<!-- Multiple Print Shipping Label Button -->
		<div v-if="canPrintMultipleShippingLabels" class="mb-3">
			<button
				class="btn btn-primary"
				@click="printMultipleShippingLabels"
				:disabled="isDownloading"
			>
				<i class="bi bi-printer"></i>
				<span>Print Shipping Labels ({{ selected.length }})</span>
			</button>
		</div>

		<!-- Multiple Print Picking List Button -->
		<div v-if="canPrintMultiplePickingLists" class="mb-3">
			<button
				class="btn btn-success"
				@click="printMultiplePickingLists"
				:disabled="isHandoverDownloading"
			>
				<i class="bi bi-printer"></i>
				<span>Print Picking Lists ({{ selected.length }})</span>
			</button>
		</div>

		<div class="submissions-list table-responsive b-0 private-order-table" ref="ordersList">
			<table
				ref="ordersListTable"
				class="table table-primary table-striped table-hover table--small submissions-list-table"
			>
				<thead>
					<tr>
						<th width="3%" class="text-center">
							<label class="input-checkbox-container">
								<input type="checkbox" class="input-checkbox" @input="selectAll" />
								<span></span>
							</label>
						</th>
						<th width="10%" class="text-left">Order</th>
						<th v-if="isAdmin" class="text-left">Seller</th>
						<th v-if="isAdmin || isDropshipper" class="text-left">Supplier</th>
						<th class="text-left">Info</th>
						<th class="text-left">Customer Data</th>
						<th width="15%" class="text-left">Pricing & Payment</th>
						<th width="8%" class="text-left">Status</th>
						<th class="text-right">Action</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="(data, i) in privateOrders" :key="i">
						<td>
							<label class="input-checkbox-container">
								<input
									type="checkbox"
									class="input-checkbox"
									:value="data.order_id"
									v-model="selected"
									@input="onSelectedData(data.order_id)"
								/>
								<span></span>
							</label>
						</td>
						<!-- order_id -->
						<td>
							<div class="cursor-pointer">
								<div class="d-flex flex-column">
									<span class="font-weight-bold text-primary">
										{{ `#${data.order_id}` }}
									</span>
									<small class="">
										<template v-if="data.created_at">
											{{ formatDateLocal(data.created_at)}}
										</template>
									</small>
								</div>
								<div class="mt-1">
									<!-- shopee -->
									<template v-if="isMarketplaceOrder(data) && getMarketplaceData(data)">
										<small>
											{{ getMarketplaceData(data).order_sn || getMarketplaceData(data).order_id }}
										</small>
									</template>
								</div>

								<div class="d-flex mt-2">
									<small>
										<button class="btn btn-light btn-sm" @click="showOrderList(data)">
											Show Orders
										</button>
									</small>
								</div>
							</div>
						</td>

						<!-- seller -->
						<td v-if="isAdmin">
							<div class="d-flex flex-column">
								<span class="font-weight-bold">{{ data.seller_name || '-' }}</span>
								<small class="text-muted">{{ data.seller_email || '-' }}</small>
							</div>
						</td>

						<!-- supplier -->
						<td v-if="isAdmin || isDropshipper">
							<div class="d-flex flex-column">
								<span class="font-weight-bold">{{ data.supplier_name || '-' }}</span>
								<!-- <small class="text-muted mb-1">{{ data.supplier_email || '-' }}</small> -->
								<small class="text-muted mb-2" v-if="data.supplier_id">ID: {{ data.supplier_id || '-' }}</small>
								<small class="text-muted mb-2" v-if="data.supplier_code">{{ data.supplier_code || '-' }}</small>

								<a
									v-if="data.supplier_phone"
									:href="waLink(data.supplier_phone)"
									target="_blank"
									class="btn btn-sm btn-outline-success d-flex align-items-center justify-content-center"
								>
									<icon name="brands/whatsapp"  class="mr-1"></icon>
									<span>Hubungi Supplier</span>
								</a>
							</div>
						</td>

						<!-- Info -->
						<td>
							<div class="d-flex flex-column">
								<!-- Marketplace -->
								<template v-if="isMarketplaceOrder(data)">
									<MarketplaceColumn
										:data="getMarketplaceData(data)"
									/>
								</template>
								<!-- Import -->
								<template v-else-if="data.order_source === 'import'">
									<div class="d-flex align-items-center">
										<i class="fi fi-rr-box mt-1"></i>
										<span class="ml-2">
											Multiple Products
										</span>
									</div>
								</template>

							</div>
						</td>

						<!-- customer data -->
						<td>
							<div class="d-flex flex-column">
								<!-- shopee -->
								<template v-if="isMarketplaceOrder(data) && getCustomerData(data)">
									{{ getCustomerData(data).name }}
								</template>
								<!-- Import -->
								<template v-else-if="data.order_source === 'import'">
									<div>
										<i class="fi fi-rr-users"></i>
										<span class="ml-2">
											Multiple Buyers
										</span>
									</div>
								</template>
							</div>
						</td>

						<!-- Pricing -->
						<td>
							<div class="pricing-card-summary">
								<div class="pricing-row pricing-total">
									<b class="pricing-label">Total</b>
									<b class="pricing-value">{{ formatMoney(calcTotalPrice(data)) }}</b>
								</div>
								<div class="pricing-row">
									<small class="pricing-label">Total Product</small>
									<span class="pricing-value">{{ formatMoney(data.total_product_price || 0) }}</span>
								</div>
								<div v-for="row in getPricingRows(data)" :key="row.label" class="pricing-row">
									<small class="pricing-label">{{ row.label }}</small>
									<span class="pricing-value">{{ formatMoney(row.value) }}</span>
								</div>
							</div>
						</td>

						<!-- Status -->
						<td>
							<div class="d-flex flex-column">
								<div class="mb-1">
									<h6 class="mb-0">
										<span class="badge text-uppercase text-large"
											:class="{
												'badge-success': data.status === 'completed',
												'badge-info': data.status === 'processing',
												'badge-light': data.status === 'pending',
												'badge-danger': data.status === 'cancelled',
											}"
										>
											{{ formattedStatus(data.status) }}
										</span>
									</h6>
								</div>
								<div>
									<span class="badge text-uppercase" :class="{
										'badge-success': data.payment.status === 'paid',
										'badge-danger': data.payment.status === 'unpaid',
										'badge-warning': data.payment.status === 'pending',
									}">
										{{ data.payment.status }}
									</span>
								</div>
							</div>
						</td>

						<!-- Action -->
						<td class="text-right">
							<div class="">
								<div class="dropdown">
									<button
										type="button"
										class="btn btn-sm btn-light dropdown-toggle"
										data-toggle="dropdown"
										data-display="static"
										aria-haspopup="true"
										aria-expanded="false"
									>
										Actions
									</button>
									<div class="dropdown-menu dropdown-menu-right">
										<!-- <button class="dropdown-item" type="button" @click="onAction('process_orders', data)">
											Process Order
										</button> -->
										<button
											class="dropdown-item"
											type="button"
											@click="onAction('download_receipt_number', data)"
										>
											Print Shipping Label
										</button>
										<button
											class="dropdown-item"
											type="button"
											@click="onAction('download_picking_list', data)"
										>
											Download Picking List
										</button>
										<button
											v-if="data.order_source === 'import'"
											class="dropdown-item"
											type="button"
											@click="onAction('download_mapping_products', data)"
										>
											Download Mapping Produk
										</button>
									</div>
								</div>
							</div>
						</td>
					</tr>
				</tbody>
			</table>

			<div
				class="list-empty table-empty text-center"
				v-if="privateOrders.length === 0 && !isLoading"
			>
				<i class="material-icons mr-2">error_outline</i>
				<span>No Orders Found</span>
			</div>

			<!-- Loader -->
			<div class="my-3 p-3 text-center" v-if="isLoading">
				<div class="is-loading dark large"></div>
			</div>
			<OrderDetailModal
				v-if="orderDetailVisible"
				:data="orderDetail"
				@close="orderDetailVisible = false"
			/>
			<EditOrderModal
				v-if="editOrderModalVisible"
				:data="orderDetail"
				:is-submit-load="isSubmitLoading"
				@close="editOrderModalVisible = false"
				@submit="updateStatus"
			/>
			<ProccessOrderModal
				v-if="proccessOrderVisible"
				:data="orderDetail"
				:is-submit-loading="isSubmitLoading"
				@close="closeModalProcessOrder"
				@success="paymentSuccess"
			/>
			<UpdateStatusModal
				v-if="isUpdateStatusModalVisible"
				:data="orderDetail"
				:type="modalType"
				:is-loading="isSubmitLoading"
				@close="isUpdateStatusModalVisible = false"
				@update="updateStatus"
				@change-rceipt-number="updateReceiptNumber"
			/>
			<OrderListModal
				v-if="orderListVisible"
				:order="selectedOrder"
				@close="orderListVisible = false"
			/>

			<div class="table-vertical-scroll hidden-sm" ref="ordersListVerticalScroll">
				<div
					class="table-vertical-scroll-content"
					ref="ordersListVerticalScrollContent"
				></div>
			</div>
		</div>
		<!-- Handover PDF Template (hidden off-screen, built via helper) -->
		<div
			v-if="handoverHtml"
			id="handover-template"
			class="handover-print-container"
			v-html="handoverHtml"
		></div>

		<!-- Packing Slip Modal -->
		<packing-slip-modal
			:store="store"
			:orders="submissionsLabels"
			:courier-logos="courierLogos"
			:oo-logo="ooLogo"
			:orderonline-logo="orderOnlineLogo"
			:team-members="teamMembers"
			:labels="labels"
			:product-codes="productCodes"
			:selected-submissions="selectedSubmissions"
			@close="closePackingSlip"
			v-if="isPackingSlipVisible"
		>
		</packing-slip-modal>

		<!-- Off-screen barcode canvas for rendering barcode images -->
		<div style="position: absolute; left: -9999px; top: -9999px;">
			<canvas id="barcode"></canvas>
		</div>

	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import html2canvas from 'html2canvas';
import * as jsPDF from 'jspdf';
import dayjs from 'dayjs';
import {
	duplicateVar,
	formatDate,
	formatMoney,
	getAxiosErrorMessage,
	downloadFile,
	makePDF,
	buildPickingListHtmlMultiple,
	buildPickingListHtmlWithMapping,
	mapPrivateOrderItemsToPickingListItems,
	waLink,
	imageURLtoBase64,
} from '@/lib/helper';
import { shippingCouriers } from '@/lib/statics';
import ordersOo from '@/api/orders-oo';
import productsApi from '@/api/products';
import PackingSlipModal from '@/components/PackingSlipModal.vue';
import OrderDetailModal from '@/components/private-orders/modal/OrderDetails.vue';
import EditOrderModal from '@/components/private-orders/modal/edit-order/index.vue';
import MarketplaceColumn from '@/components/private-orders/column/Marketplace.vue';
import ProccessOrderModal from '@/components/private-orders/modal/proccess-order/index.vue';
import UpdateStatusModal from '@/components/private-orders/modal/UpdateStatus.vue';
import OrderListModal from '@/components/private-orders/modal/OrderList.vue';
import { updateDataOrder, processDataOrder, adminUpdateOrder, downloadShippingLabel, getPrivateOrderMappingProducts } from '@/api/private-order';
import {
	getList as getPrivateOrderItemsList,
} from '@/api/private-order-items';

export default {
	name: 'PrivateOrderTable',
	props: {
		items: {
			type: Array,
			default: () => [],
		},
		isLoading: {
			type: Boolean,
			default: () => false,
		},
		type: {
			type: String,
			default: '',
		},
		showModalProcessOrder: {
			type: Boolean,
			default: false,
		},
	},
	components: {
		OrderDetailModal,
		EditOrderModal,
		ProccessOrderModal,
		UpdateStatusModal,
		MarketplaceColumn,
		OrderListModal,
		PackingSlipModal,
	},
	data() {
		return {
			tHeaders: [],
			selected: [],
			orderDetailVisible: false,
			editOrderModalVisible: false,
			proccessOrderVisible: false,
			isUpdateStatusModalVisible: false,
			proccessOrderType: '',
			orderDetail: null,
			modalType: '',
			isSubmitLoading: false,
			paymentChecker: null,
			payment: null,
			orderListVisible: false,
			selectedOrder: null,
			isDownloading: false,
			handoverOrder: null,
			isHandoverDownloading: false,
			handoverHtml: '',
			isShowPackingSlip: false,
			submissionsLabels: [],
			productCodes: {},
			labels: [],
			selectedSubmissions: [],
			courierLogos: [],
			ooLogo: null,
			orderOnlineLogo: null,
			labelsFetchingStatus: [],
		};
	},
	computed: {
		...mapGetters({
			privateOrders: 'monitoringPrivateOrder/privateOrders',
			isAdmin: 'isAdmin',
			isSupplier: 'isSupplier',
			isDropshipper: 'isDropshipper',
			selectedData: 'monitoringPrivateOrder/selectedData',
			store: 'store',
			teamMembers: 'members/all',
		}),
		isPaid() {
			return this.payment?.status === 'paid';
		},
		isPackingSlipVisible() {
			const isSourcesLoaded = this.submissionsLabels.length === this.selectedSubmissions.length;
			return this.isShowPackingSlip && isSourcesLoaded && (this.ooLogo && this.orderOnlineLogo);
		},
		isFetchingLabel() {
			return this.labelsFetchingStatus.length > 0;
		},
		canPrintMultipleShippingLabels() {
			// Can only print multiple if all selected orders are orderonline marketplace
			if (this.selected.length === 0) return false;

			const selectedOrders = this.privateOrders.filter(order => this.selected.includes(order.order_id));

			if (selectedOrders.length === 0) return false;

			// Check if all selected orders are orderonline marketplace
			return selectedOrders.every(order => this.isOrderonlineOrder(order));
		},
		canPrintMultiplePickingLists() {
			// Can print multiple picking lists if at least 2 orders are selected
			return this.selected.length >= 2;
		},
	},
	created() {
		this.setVerticalScroll();
		this.init();
		document.addEventListener('scroll', this.onDocumentScroll, true);
		window.addEventListener('resize', this.checkListTable, true);
		this.loadCourierImages();
		this.loadOOIcon();
		this.loadOrderOnlineLogo();
	},
	destroyed() {
		document.removeEventListener('scroll', this.onDocumentScroll, true);
		window.removeEventListener('resize', this.checkListTable, true);
	},
	updated() {
		this.$nextTick(() => {
			this.setVerticalScroll();
			this.checkListTable();
		});
	},
	watch: {
		showModalProcessOrder(val) {
			this.proccessOrderVisible = val;
		},
	},
	methods: {
		...mapActions({
			updateDataPrivateOrder: 'monitoringPrivateOrder/updateData',
			setDetailOrder: 'monitoringPrivateOrder/setDetailOrder',
			setSelectedData: 'monitoringPrivateOrder/setSelectedData',
			addToSelected: 'monitoringPrivateOrder/addToSelected',
			removeFromSelected: 'monitoringPrivateOrder/removeFromSelected',
		}),
		duplicateVar,
		formatMoney,
		init() {
			if (this.isSupplier || this.type === 'supplier') {
				this.tHeaders = [
					{ key: 'order_id', label: 'Order Id' },
					{ key: 'customer', label: 'Customer' },
					{ key: 'product', label: 'Product' },
					{ key: 'marketplace', label: 'Marketplace' },
					{ key: 'sku', label: 'Product Sku' },
					{ key: 'quantity', label: 'Quantity' },
					{ key: 'base_product_price', label: 'Product Price HPP' },
					{ key: 'total_base_product_price', label: 'Total Product Price HPP' },
					{ key: 'shipping_cost', label: 'Shipping Cost' },
					{ key: 'other_cost', label: 'Other Cost' },
					{ key: 'total_price', label: 'Total Price' },
					{ key: 'city', label: 'City' },
					{ key: 'status', label: 'Status' },
					{ key: 'order_type', label: 'Order Type' },
					{ key: 'payment_status', label: 'Payment Status' },
					{ key: 'payment_info', label: 'Payment Info' },
					{ key: 'payment_type', label: 'Payment Type' },
					{ key: 'receipt_number', label: 'Receipt Number' },
					{ key: 'created_at', label: 'Created At' },
					{ key: 'processing_at', label: 'Processing At' },
					{ key: 'proof_of_shipping', label: 'Proof of Shipping' },
					{ key: 'proof_of_delivery', label: 'Proof of Delivery' },
					{ key: 'actions', label: 'Actions' },
				];
			}

			if (this.isDropshipper || this.type === 'dropshipper') {
				this.tHeaders = [
					{ key: 'order_id', label: 'Order Id' },
					{ key: 'customer', label: 'Customer' },
					{ key: 'product', label: 'Product' },
					{ key: 'marketplace', label: 'Marketplace' },
					{ key: 'sku', label: 'Product Sku' },
					{ key: 'quantity', label: 'Quantity' },
					{ key: 'product_price', label: 'Product Price' },
					{ key: 'total_price', label: 'Total Product Price' },
					{ key: 'shipping_cost', label: 'Shipping Cost' },
					{ key: 'other_cost', label: 'Other Cost' },
					{ key: 'total_mount_price', label: 'Total Price' },
					{ key: 'city', label: 'City' },
					{ key: 'status', label: 'Status' },
					{ key: 'order_type', label: 'Order Type' },
					{ key: 'payment_status', label: 'Payment Status' },
					{ key: 'payment_info', label: 'Payment Info' },
					{ key: 'payment_type', label: 'Payment Type' },
					{ key: 'receipt_number', label: 'Receipt Number' },
					{ key: 'created_at', label: 'Created At' },
					{ key: 'processing_at', label: 'Processing At' },
					{ key: 'proof_of_shipping', label: 'Proof of Shipping' },
					{ key: 'proof_of_delivery', label: 'Proof of Delivery' },
					{ key: 'actions', label: 'Actions' },
				];
			}

			if (this.type === 'admin') {
				this.tHeaders = [
					{ key: 'order_id', label: 'Order Id' },
					{ key: 'customer', label: 'Customer' },
					{ key: 'product', label: 'Product' },
					{ key: 'marketplace', label: 'Marketplace' },
					{ key: 'sku', label: 'Product Sku' },
					{ key: 'quantity', label: 'Quantity' },
					{ key: 'base_product_price', label: 'Product Price HPP' },
					{ key: 'total_base_product_price', label: 'Total Product Price HPP' },
					{ key: 'product_price', label: 'Product Price' },
					{ key: 'total_price_product', label: 'Total Product Price' },
					{ key: 'other_cost', label: 'Other Cost' },
					{ key: 'shipping_cost', label: 'Shipping Cost' },
					{ key: 'total_price', label: 'Total Price' },
					{ key: 'city', label: 'City' },
					{ key: 'status', label: 'Status' },
					{ key: 'order_type', label: 'Order Type' },
					{ key: 'payment_status', label: 'Payment Status' },
					{ key: 'payment_info', label: 'Payment Info' },
					{ key: 'payment_type', label: 'Payment Type' },
					{ key: 'receipt_number', label: 'Receipt Number' },
					{ key: 'created_at', label: 'Created At' },
					{ key: 'processing_at', label: 'Processing At' },
					{ key: 'proof_of_shipping', label: 'Proof of Shipping' },
					{ key: 'proof_of_delivery', label: 'Proof of Delivery' },
					{ key: 'actions', label: 'Actions' },
				];
			}
		},
		waLink,
		onSelectedData(orderId) {
			const isExistIndex = this.selectedData.findIndex(id => id === orderId);
			if (isExistIndex !== -1) {
				this.removeFromSelected(orderId);
			} else {
				this.addToSelected(orderId);
			}

			this.selected = this.selectedData;
		},
		selectAll() {
			this.selected = this.selected.length !== this.privateOrders.length
				? this.privateOrders.map(item => item.order_id)
				: [];

			this.setSelectedData(this.selected); // Mengganti seluruh array dengan options baru
		},
		showDetailOrders(product) {
			this.orderDetailVisible = !this.orderDetailVisible;
			this.orderDetail = product;
		},
		onAction(key, data) {
			if (key === 'edit_order') {
				this.editOrderModalVisible = true;
				this.orderDetail = data;
			} else if (key === 'process_orders') {
				this.proccessOrderVisible = true;
				this.proccessOrderType = data.order_type;
				this.orderDetail = data;
			} else if (key === 'mark_as_received' || key === 'mark_as_delivery') {
				this.isUpdateStatusModalVisible = true;
				this.orderDetail = data;
				this.modalType = 'confirm';
			} else if (key === 'input_receipt_number') {
				this.isUpdateStatusModalVisible = true;
				this.orderDetail = data;
				this.modalType = 'input_receipt_number';
			} else if (key === 'update_status') {
				this.isUpdateStatusModalVisible = true;
				this.orderDetail = data;
				this.modalType = key;
			} else if (key === 'download_receipt_number') {
				this.downloadShippingDocument(data);
			} else if (key === 'download_picking_list') {
				this.downloadPickingListDocument(data);
			} else if (key === 'print_shipping_label') {
				this.printShippingLabel(data);
			} else if (key === 'download_mapping_products') {
				this.downloadMappingProducts(data);
			}
		},
		updateStatus(value, type) {
			this.isSubmitLoading = true;
			const params = {};

			if (type === 'edit_order') {
				params.customer_data = {
					name: value?.[0]?.form?.name ?? null,
					phone: value?.[0]?.form?.phone ?? null,
					email: value?.[0]?.form?.email ?? null,
					address: value?.[0]?.form?.address ?? null,
					province_id: value?.[0]?.form?.shipping?.province?.value?.key ?? null,
					province_name: value?.[0]?.form?.shipping?.province?.value?.label ?? null,
					city_id: value?.[0]?.form?.shipping?.city?.value?.key ?? null,
					city_name: value?.[0]?.form?.shipping?.city?.value?.label ?? null,
					subdistrict_name: value?.[0]?.form?.shipping?.subdistrict?.value?.label ?? null,
					subdistrict_id: value?.[0]?.form?.shipping?.subdistrict?.value?.key ?? null,
					zip: value?.[0]?.form?.shipping.zip ?? null,
				};

				params.order_data = {
					quantity: value?.[1]?.form?.quantity,
					notes: value?.[1]?.form?.notes,
					payment_method: value?.[1]?.form?.payment_method?.value?.key,
				};

				params.type = 'edit_order';
			}

			if (type === 'update_status') {
				params.status = value?.key;
			}

			if (type === 'input_receipt_number') {
				params.receipt_number = value.receiptNumber;
				params.cancelled_receipt_number = value.cancelledReceiptNumber;
				params.pos_image_url = value.posImage.url;
				params.type = this.modalType;
			}

			if (type === 'confirm') {
				params.status = value?.key;
				if (this.isDropshipper) {
					params.pod_image_url = value?.podImage.url;
				} else if (!this.orderDetail.shipping.receipt_number) {
					params.pos_image_url = value.posImage.url;
				}
			}

			const id = this.orderDetail?.action || this.orderDetail?._id;
			const callback = (res) => {
				const payload = {
					id,
					role_type: this.type,
					...res?.data?.data,
				};

				if (payload && res?.data?.message === 'success') {
					this.updateDataPrivateOrder(payload);
					this.$notify({
						group: 'app',
						title: 'Wholesale Order',
						text: 'Updated Status Successfully',
						type: 'success',
					});
				} else {
					const message = res?.data?.message;

					this.$notify({
						group: 'app',
						title: 'Wholesale Order',
						text: message,
						type: 'error',
					});
				}

				this.isUpdateStatusModalVisible = false;
				this.editOrderModalVisible = false;
				this.$emit('trigger-summary');
				this.isSubmitLoading = false;
			};

			const err = (e) => {
				console.log(e);
				this.$notify({
					group: 'app',
					title: 'Wholesale Order',
					text: 'Updated Status Successfully',
					type: 'success',
				});
			};

			if (this.type === 'admin') {
				adminUpdateOrder(id, params, callback, err);
			} else {
				updateDataOrder(id, params, callback, err);
			}
		},
		updateReceiptNumber(receiptNumber, cancelledReceiptNumber, posImage) {
			const params = {
				receiptNumber,
				cancelledReceiptNumber,
				posImage,
			};

			this.updateStatus(params, 'input_receipt_number');
		},
		processOrder(dataParams) {
			this.isSubmitLoading = true;
			const id = this.orderDetail?.action || this.orderDetail?._id;
			const params = {
				type: dataParams.type,
				shipping_cost: dataParams.shipping_cost,
				other_cost: dataParams?.other_cost,
				total_payment: dataParams?.total_payment ?? 0,
				due_date: dataParams?.due_date ?? null,
				payment_method: dataParams?.payment_method ?? null,
			};

			if (dataParams.type === 'private_order_dp') {
				params.total_payment_settlement = dataParams?.dp.total_payment_settlement;
				params.due_date_settlement = dataParams?.dp.due_date_settlement;
				params.total_payment = dataParams?.dp.total_payment;
				params.due_date = dataParams?.dp.due_date;
			}

			const callback = (res) => {
				if (res.data.error_code === 0) {
					const payload = {
						id,
						...res?.data?.data,
					};
					this.updateDataPrivateOrder(payload);

					this.$notify({
						group: 'app',
						title: 'Wholesale Order',
						text: 'Process Order Successfully',
						type: 'succes',
					});
				} else {
					this.$notify({
						group: 'app',
						title: 'Wholesale Order',
						text: res.data.message,
						type: 'error',
					});
				}

				this.isSubmitLoading = false;
				this.proccessOrderVisible = false;
			};
			const err = (e) => {
				console.log(e.response.data.message);
				this.$notify({
					group: 'app',
					title: 'Wholesale Order',
					text: e.response.data.message,
					type: 'error',
				});
				this.isSubmitLoading = false;
				this.proccessOrderVisible = false;
			};

			processDataOrder(id, params, callback, err);
		},
		onUpdateDataOrder(forms) {
			const id = this.orderDetail.action;
			const params = {
				customer_data: {
					name: forms?.[0]?.form?.name ?? null,
					phone: forms?.[0]?.form?.phone ?? null,
					email: forms?.[0]?.form?.email ?? null,
					address: forms?.[0]?.form?.address ?? null,
					province_id: forms?.[0]?.form?.shipping?.province?.value?.key ?? null,
					province_name: forms?.[0]?.form?.shipping?.province?.value?.label ?? null,
					city_id: forms?.[0]?.form?.shipping?.city?.value?.key ?? null,
					city_name: forms?.[0]?.form?.shipping?.city?.value?.label ?? null,
					subdistrict_name: forms?.[0]?.form?.shipping?.subdistrict?.value?.label ?? null,
					subdistrict_id: forms?.[0]?.form?.shipping?.subdistrict?.value?.key ?? null,
					zip: forms?.[0]?.form?.shipping.zip ?? null,
				},
				order_data: {
					quantity: forms?.[1]?.form?.quantity,
					notes: forms?.[1]?.form?.notes,
					payment_method: forms?.[1]?.form?.payment_method?.value?.key,
				},
			};

			const callback = () => {
				//
			};
			const err = e => console.log(e);

			updateDataOrder(id, params, callback, err);
		},
		updateDataOrder(forms) {
			console.log('forms -> ', forms);
		},
		onDocumentScroll(event) {
			if (event.target === document) {
				this.checkListTable();
			} else if (event.target === this.$refs.ordersList) {
				this.$refs.ordersListVerticalScroll.scrollLeft = this.$refs.ordersList.scrollLeft;
			} else if (event.target === this.$refs.ordersListVerticalScroll) {
				this.$refs.ordersList.scrollLeft = this.$refs.ordersListVerticalScroll.scrollLeft;
			}
		},
		checkListTable() {
			if (this.$refs.ordersList) {
				this.$refs.ordersListVerticalScroll.style.display = 'block';
			}
		},
		setVerticalScroll() {
			if (this.$refs.ordersList) {
				const listWidth = this.$refs.ordersList.offsetWidth;
				const tableWidth = this.$refs.ordersListTable.offsetWidth;
				this.$refs.ordersListVerticalScroll.style.width = `${listWidth}px`;
				this.$refs.ordersListVerticalScrollContent.style.width = `${tableWidth}px`;
			}
		},
		closeModalProcessOrder() {
			this.proccessOrderVisible = false;
			this.$emit('close-modal-process-order');
		},
		paymentSuccess() {
			this.$emit('trigger-summary');
			this.$emit('trigger-fetch-data');
		},
		formatDateLocal(createdAt) {
			const dateFormatted = formatDate(this.moment, createdAt, 'DD-MM-YYYY HH:mm');
			return dateFormatted;
		},
		getMarketplaceData(data) {
			const orders = data.order_items || [];
			if (orders.length <= 0) {
				return {};
			}
			const order = orders[0].marketplace_data || {};
			return order;
		},
		getCustomerData(data) {
			const orders = data.order_items || [];
			if (orders.length <= 0) {
				return {};
			}
			return orders[0].customer_data || {};
		},
		showOrderList(order) {
			// console.log('order: ', order);
			this.orderListVisible = true;
			this.selectedOrder = order;
		},
		downloadPickingListDocument(order) {
			if (!order) return;

			const orderId = order?.order_id;
			if (!orderId) return;

			const params = {
				page: 1,
				limit: 1000,
				orderId,
			};
			this.isHandoverDownloading = true;

			const callback = (res) => {
				const data = res?.data?.data;
				const orderItems = data || [];

				const items = mapPrivateOrderItemsToPickingListItems(orderItems);
				const totalQty = items.reduce((sum, item) => sum + item.quantity, 0);
				const printedAt = dayjs().format('YYYY-MM-DD HH:mm:ss');
				this.handoverHtml = buildPickingListHtmlMultiple({ orders: [order], groupedItems: items, totalQty, printedAt });

				this.$nextTick(() => {
					try {
						const orderId = order.order_id || '';
						const timestamp = new Date().getTime();
						const name = `Picking-List-${orderId}-${timestamp}`;
						const callback = () => {
							this.isHandoverDownloading = false;
						};

						makePDF(document, 'handover-template', html2canvas, jsPDF, name, callback);
					} catch (error) {
						console.log('error downloading picking list', error);
						this.isHandoverDownloading = false;
						this.handoverHtml = null;
					}
				});
			};

			const err = (e) => {
				console.log('Error fetching order items:', e);
				this.isHandoverDownloading = false;
			};

			getPrivateOrderItemsList(params, callback, err);
		},
		downloadMappingProducts(order) {
			// Download mapping products - for import orders only
			this.isHandoverDownloading = true;

			const mappingCallback = (mappingRes) => {
				const mappingData = mappingRes?.data?.data || [];
				this.downloadPickingListDocumentWithMapping(order, mappingData);
			};

			const mappingErr = (e) => {
				console.log('Error fetching mapping products:', e);
				this.isHandoverDownloading = false;
			};

			getPrivateOrderMappingProducts(order.action, mappingCallback, mappingErr);
		},
		downloadShippingDocument(order) {
			if (!order) return;
			if (this.isDownloading) return;

			// orderonline - use the new packing slips printing function
			if (this.isOrderonlineOrder(order)) {
				this.showPackingSlips([order]);
				return;
			}

			// shopee
			if (order.order_source === 'shopee') {
				this.downloadShopeeShippingDocument(order);
			} else {
				downloadFile(order.shipping.label_file_url, true);
			}
		},
		downloadShopeeShippingDocument(order) {
			if (!order) return;
			try {
				if (this.isDownloading) return;
				if (order.order_source !== 'shopee') return;
				const marketplaceOrder = order.order_items[0] || [];
				const orderSn = marketplaceOrder.marketplace_data.order_sn ?? '';
				const platform = marketplaceOrder.marketplace_data.platform ?? '';
				const params = {
					order_sn: orderSn,
					platform,
				};
				this.isDownloading = true;

				const callback = (response) => {
					this.isDownloading = false;
					const fileUrl = response?.data?.data.file ?? '';
					const fileBase64 = `data:application/pdf;base64,${fileUrl}`;
					downloadFile(fileBase64);
				};

				const errorCallback = (err) => {
					this.isDownloading = false;
					const errorMessage = getAxiosErrorMessage(err);
					console.log(errorMessage);
				};

				downloadShippingLabel(params, callback, errorCallback);
			} catch (error) {
				console.log(error);
			}
		},
		/**
		 * Download OrderOnline Shippling Label
		 * @param array orders
		 */
		downloadOrderonlineShippingLabel(orders = []) {
			if (!orders || orders.length === 0) return;

			try {
				if (this.isDownloading) return;

				// Handle both single order and array of orders
				const ordersArray = Array.isArray(orders) ? orders : [orders];

				// Extract order IDs
				const orderIds = ordersArray.map(order => order._id).filter(id => id);

				if (orderIds.length === 0) {
					this.$notify({
						group: 'app',
						type: 'error',
						title: 'Error',
						text: 'Order ID tidak ditemukan.',
					});
					return;
				}

				const params = {
					order_ids: orderIds,
					platform: 'orderonline',
				};

				this.isDownloading = true;

				const callback = (response) => {
					this.isDownloading = false;
					const fileData = response?.data?.data?.pdf || response?.data?.data?.file || '';
					if (fileData) {
						const fileBase64 = `data:application/pdf;base64,${fileData}`;

						// Generate filename: "Shipping Label - YYYY-MM-DD HH-MM"
						const now = new Date();
						const date = now.toISOString().split('T')[0]; // YYYY-MM-DD
						const time = now.toTimeString().split(' ')[0].replace(/:/g, '-'); // HH-MM-SS
						const filename = `Shipping Label - ${date} ${time}`;

						downloadFile(fileBase64, false, filename);
					}
				};

				const errorCallback = (err) => {
					this.isDownloading = false;
					const errorMessage = getAxiosErrorMessage(err);
					console.log(errorMessage);
					this.$notify({
						group: 'app',
						type: 'error',
						title: 'Error',
						text: 'Gagal mendownload shipping label. Silakan coba lagi.',
					});
				};

				downloadShippingLabel(params, callback, errorCallback);
			} catch (error) {
				console.log(error);
				this.isDownloading = false;
			}
		},
		loadCourierImages() {
			shippingCouriers.push({ key: 'jx', label: 'JX' });
			shippingCouriers.forEach((courier) => {
				const callback = (response) => {
					this.courierLogos.push({ key: courier.key, ...response });
				};
				const url = `/images/logo-${courier.key}.png`;
				imageURLtoBase64(url, callback);
			});
		},
		loadOOIcon() {
			const callback = (response) => {
				this.ooLogo = response;
			};
			const url = '/img/ostock.svg';
			imageURLtoBase64(url, callback);
		},
		loadOrderOnlineLogo() {
			const callback = (response) => {
				this.orderOnlineLogo = response;
			};
			const url = '/img/orderonline.svg';
			imageURLtoBase64(url, callback);
		},
		showPackingSlips(orders) {
			const mappedOrders = orders.map((order) => {
				const firstItem = order.order_items?.[0] || {};
				const productId = firstItem.product_id?.$oid || firstItem.product_id || '';
				return {
					_id: order.action || order._id,
					order_id: order.order_id,
					source_type: 'orderonline',
					source_id: this.getMarketplaceData(order)?.order_sn || '',
					type: 'form',
					product_id: productId,
					shipping: order.shipping || {},
					customer_data: order.customer_data || {},
				};
			});

			this.selectedSubmissions = mappedOrders;
			this.submissionsLabels = [];
			this.labels = [];
			this.productCodes = {};
			this.labelsFetchingStatus = [];

			this.fetchProductCodes(this.selectedSubmissions);
			this.fetchLabelDataSource(this.selectedSubmissions);
		},
		fetchLabelDataSource(orders) {
			const callback = (response, order) => {
				const orderData = response.data;
				if (order) {
					orderData._id = order._id;
					orderData.source_type = order.source_type;
					orderData.source_id = order.source_id;
				}
				this.submissionsLabels.push(orderData);
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				console.log(message);
			};

			for (let index = 0; index < orders.length; index++) {
				const order = orders[index];
				const id = order.source_id;
				const callbackWrapper = (response) => {
					callback(response, order);
				};
				if (order.source_type === 'orderonline') {
					ordersOo.getOrderDetails(id, callbackWrapper, errorCallback);
				}
			}
		},
		fetchProductCodes(orders) {
			this.labelsFetchingStatus.push('code');
			const productIds = [];
			for (let index = 0; index < orders.length; index++) {
				const order = orders[index];
				if (order.type === 'storefront') {
					const cartItems = order.cart_items || [];
					for (let itemIndex = 0; itemIndex < cartItems.length; itemIndex++) {
						const cartItem = cartItems[itemIndex];
						const cartItemProductId = cartItem.product_id;
						if (cartItemProductId && cartItemProductId.$oid) {
							const productId = cartItemProductId.$oid;
							const idIndex = productIds.findIndex(id => id === productId);
							if (idIndex === -1) {
								productIds.push(productId);
							}
						}
					}
				} else if (order.type === 'form') {
					const productId = order.product_id;
					const idIndex = productIds.findIndex(id => id === productId);
					if (idIndex === -1) {
						productIds.push(productId);
					}
				}
			}
			const params = {
				ids: JSON.stringify(productIds),
			};
			const callback = (response) => {
				const productCodes = response.data;
				this.productCodes = productCodes;
				const index = this.labelsFetchingStatus.findIndex(status => status === 'code');
				if (index !== -1) {
					this.labelsFetchingStatus.splice(index, 1);
				}
				if (this.labelsFetchingStatus.length === 0) {
					this.isShowPackingSlip = true;
				}
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				console.log(message);
				const index = this.labelsFetchingStatus.findIndex(status => status === 'code');
				if (index !== -1) {
					this.labelsFetchingStatus.splice(index, 1);
				}
			};
			productsApi.getCodes(params, callback, errorCallback);
		},
		closePackingSlip() {
			this.isShowPackingSlip = false;
		},
		downloadPickingListDocumentWithMapping(order, mappingData = []) {
			if (!order) return;

			// Calculate total quantity from mapping data
			const printedAt = dayjs().format('YYYY-MM-DD HH:mm:ss');

			// Build HTML with mapping structure
			this.handoverHtml = buildPickingListHtmlWithMapping({ order, mappingData, printedAt });

			this.$nextTick(() => {
				try {
					const orderId = order.order_id || '';
					const timestamp = new Date().getTime();
					const name = `mapping-products-private-order-${orderId}-${timestamp}`;
					const callback = () => {
						this.isHandoverDownloading = false;
					};

					makePDF(document, 'handover-template', html2canvas, jsPDF, name, callback);
				} catch (error) {
					console.log('error downloading picking list', error);
					this.isHandoverDownloading = false;
					this.handoverHtml = null;
				}
			});
		},
		formattedStatus(status) {
			if (!status) return '';
			if (status === 'completed') return 'Delivered';
			if (status === 'processing') return 'Processing';
			if (status === 'pending') return 'Pending';
			if (status === 'cancelled') return 'Cancelled';
			return status;
		},
		calcTotalPrice(order) {
			const productPrice = Number(order.total_product_price || 0);

			if (this.type === 'admin') {
				// Admin: hitung dari fee_config (SHF + OHF) + service_fee
				const shf = Number(order.fee_config?.handling_fee?.total_fee_supplier || 0);
				const ohf = Number(order.fee_config?.handling_fee?.total_margin_platform || 0);
				const serviceFee = Number(order.service_fee || 0);
				return productPrice + shf + ohf + serviceFee;
			}

			// Dropshipper: handling_fee = OHF + SHF gabung dari backend
			if (this.isDropshipper || this.type === 'dropshipper') {
				const serviceFee = Number(order.service_fee || 0);
				const handlingFee = Number(order.handling_fee || 0);
				return productPrice + serviceFee + handlingFee;
			}

			// Supplier: handling_fee = SHF saja dari backend, service_fee dihapus backend
			const handlingFee = Number(order.handling_fee || 0);
			return productPrice + handlingFee;
		},
		getPricingRows(order) {
			if (this.type === 'admin') {
				return [
					order.service_fee && { label: 'Service Fee', value: order.service_fee },
					order.fee_config?.handling_fee && { label: 'SHF', value: order.fee_config.handling_fee.total_fee_supplier },
					order.fee_config?.handling_fee && { label: 'OHF', value: order.fee_config.handling_fee.total_margin_platform },
				].filter(Boolean);
			}
			if (this.isDropshipper || this.type === 'dropshipper') {
				return [
					{ label: 'Service Fee', value: order.service_fee || 0 },
					order.handling_fee && { label: 'Handling Fee', value: order.handling_fee },
				].filter(Boolean);
			}
			// Supplier
			return [
				order.handling_fee && { label: 'Handling Fee', value: order.handling_fee },
			].filter(Boolean);
		},
		isMarketplaceOrder(order) {
			return order?.order_source !== 'import';
		},
		isOrderonlineOrder(order) {
			if (!order) return false;
			if (!this.isMarketplaceOrder(order)) return false;

			const marketplaceData = this.getMarketplaceData(order);
			return marketplaceData?.platform === 'orderonline';
		},
		printMultipleShippingLabels() {
			// Get all selected orders
			const selectedOrders = this.privateOrders.filter(order => this.selected.includes(order.order_id));

			if (selectedOrders.length === 0) return;

			// Check if all selected orders are orderonline
			const allOrderonline = selectedOrders.every(order => this.isOrderonlineOrder(order));

			if (allOrderonline) {
				// Use frontend packing slips printing
				this.showPackingSlips(selectedOrders);
			} else {
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Error',
					text: 'Hanya bisa print multiple shipping labels untuk order Orderonline.',
				});
			}
		},
		printMultiplePickingLists() {
			// Get all selected orders
			const selectedOrders = this.privateOrders.filter(order => this.selected.includes(order.order_id));

			if (selectedOrders.length === 0) return;

			this.isHandoverDownloading = true;

			// Fetch order items for all selected orders
			const promises = selectedOrders.map(order => new Promise((resolve, reject) => {
				const params = {
					page: 1,
					limit: 1000,
					orderId: order.order_id,
				};

				const callback = (res) => {
					const data = res?.data?.data || [];
					resolve({ order, items: data });
				};

				const err = (e) => {
					console.log('Error fetching order items:', e);
					reject(e);
				};

				getPrivateOrderItemsList(params, callback, err);
			}));

			Promise.all(promises)
				.then((results) => {
					// Flatten all items from all orders
					const allItems = [];
					results.forEach((result) => {
						const items = mapPrivateOrderItemsToPickingListItems(result.items);
						allItems.push(...items);
					});
					const groupedItemsMap = new Map();
					allItems.forEach((item) => {
						const key = item.sku;
						if (groupedItemsMap.has(key)) {
							const existing = groupedItemsMap.get(key);
							existing.quantity += item.quantity;
						} else {
							groupedItemsMap.set(key, item);
						}
					});

					// Convert map to array
					const groupedItems = Array.from(groupedItemsMap.values());

					// Calculate total quantity
					const totalQty = groupedItems.reduce((sum, item) => sum + item.quantity, 0);

					const printedAt = dayjs().format('YYYY-MM-DD HH:mm:ss');

					// Build HTML with grouped data
					this.handoverHtml = buildPickingListHtmlMultiple({
						orders: selectedOrders,
						groupedItems,
						totalQty,
						printedAt,
					});

					this.$nextTick(() => {
						try {
							const timestamp = new Date().getTime();
							const name = `Picking-List-Multiple-${timestamp}`;
							const callback = () => {
								this.isHandoverDownloading = false;
							};

							makePDF(document, 'handover-template', html2canvas, jsPDF, name, callback);
						} catch (error) {
							console.log('error downloading multiple picking lists', error);
							this.isHandoverDownloading = false;
							this.handoverHtml = null;
						}
					});
				})
				.catch((error) => {
					console.log('Error fetching order items:', error);
					this.isHandoverDownloading = false;
					this.$notify({
						group: 'app',
						type: 'error',
						title: 'Error',
						text: 'Gagal mengambil data order items. Silakan coba lagi.',
					});
				});
		},
	},
};
</script>

<style lang="scss">
.private-order-table {
	// tbody {
	// 	tr {
	// 		&:hover {
	// 			.btn-orderid {
	// 				opacity: 1;
	// 			}
	// 		}
	// 	}
	// }

	.badge {
		padding: 0.5em 0.9em;
		border-radius: 0.5rem;
	}

	.badge-light {
		color: #212529;
		background-color: #f8f9fa;
		border: 1px solid #adadad;
	}

	.pricing-card-summary {
		background-color: #f8f9fa;
		border: 1px solid #e9ecef;
		border-radius: 8px;
		padding: 6px 10px;
		min-width: 160px;

		.pricing-row {
			display: flex;
			justify-content: space-between;
			align-items: center;
			padding: 3px 0;

			&:not(:last-child) {
				border-bottom: 1px solid #e9ecef;
			}

			.pricing-label {
				color: #6c757d;
				font-size: 11px;
				margin: 0;
			}

			.pricing-value {
				color: #212529;
				font-size: 11px;
				font-weight: 500;
			}

			&.pricing-total {
				border-bottom: 2px solid #dee2e6;
				padding-bottom: 6px;
				margin-bottom: 3px;

				.pricing-label,
				.pricing-value {
					color: #212529;
					font-weight: 600;
					font-size: 12px;
				}
			}
		}
	}
}

.handover-print-container {
	position: fixed;
	top: -10000px;
	left: -10000px;
	width: 960px;
	background-color: #ffffff;
	color: #000000;
}
</style>
